import { format } from "date-fns";

// status of the document TRAN-DOC-003
export const STATUS_PERMINTAAN_MINYAK = {
  0: { label: "Draft", severity: "secondary" },
  1: { label: "Diajukan", severity: "info" },
  2: { label: "Disetujui", severity: "success" },
  3: { label: "Diproses Transport", severity: "warning" },
  4: { label: "Diterima Unit", severity: "success" },
  5: { label: "Selesai Arsip", severity: "success" },
  "-1": { label: "Ditolak", severity: "danger" },
};

export const STATUS_OPTIONS = [
  { label: "Semua", value: "" },
  ...Object.keys(STATUS_PERMINTAAN_MINYAK)
    .sort((a, b) => Number(a) - Number(b))
    .map((key) => ({
      label: STATUS_PERMINTAAN_MINYAK[key].label,
      value: Number(key),
    })),
];

export const getStatusInfo = (status) =>
  STATUS_PERMINTAAN_MINYAK[String(status ?? 0)] ?? {
    label: "-",
    severity: "secondary",
  };

// every signature (label of /api/user/verif = permintaan_minyak:<name>)
export const VERIF_NAMES = [
  "diajukan_oleh",
  "dianalisa_oleh",
  "diketahui_transport",
  "diproses_oleh",
  "diketahui_unit",
  "diterima",
  "diarsip_oleh",
];

// the sections of the form and the fields that are saved together
export const SECTION_FIELDS = {
  pengajuan: [
    "nomor",
    "tanggal",
    "department_id",
    "jenis_minyak_id",
    "jumlah_diminta",
    "penggunaan",
  ],
  analisis: [
    "is_cek_kartu_stok",
    "is_cek_form_penggunaan_alat",
    "status_analisis",
    "jumlah_disetujui",
    "alasan_penolakan",
  ],
  pembelian: ["tanggal_beli", "bon_spbu", "gambar_bon_spbu", "diserahkan_tanggal"],
  penerimaan: ["jumlah_diterima", "keterangan_penerimaan"],
  kelengkapan: [
    "is_doc_form_permintaan",
    "is_doc_bon_spbu_asli",
    "is_doc_npp",
    "nomor_npp",
  ],
};

// which section a signature belongs to, and whether the section must be complete to sign
export const SIGN_SECTION = {
  diajukan_oleh: { section: "pengajuan", strict: true },
  dianalisa_oleh: { section: "analisis", strict: true },
  diketahui_transport: { section: "pembelian", strict: false },
  diproses_oleh: { section: "pembelian", strict: true },
  diketahui_unit: { section: "penerimaan", strict: false },
  diterima: { section: "penerimaan", strict: true },
  diarsip_oleh: { section: "kelengkapan", strict: true },
};

// the status in which each section is the one being worked on
const ACTIVE_STATUS = {
  pengajuan: 0,
  analisis: 1,
  pembelian: 2,
  penerimaan: 3,
  kelengkapan: 4,
};

// the signature that closes each section
const CLOSING_VERIF = {
  pengajuan: "verif_diajukan_oleh_id",
  analisis: "verif_dianalisa_oleh_id",
  pembelian: "verif_diproses_oleh_id",
  penerimaan: "verif_diterima_id",
  kelengkapan: "verif_diarsip_oleh_id",
};

/**
 * stage of every section: "active" (can be edited / signed), "done" (closed by its signature),
 * "pending" (not reached yet) or "cancelled" (the request is rejected).
 */
export const getSectionStages = (data) => {
  const isNew = !data || !data.id;
  const status = data?.status ?? 0;
  const stages = {};

  Object.keys(ACTIVE_STATUS).forEach((section) => {
    if (isNew) {
      stages[section] = section === "pengajuan" ? "active" : "pending";
    } else if (data[CLOSING_VERIF[section]]) {
      stages[section] = "done";
    } else if (status === -1) {
      stages[section] = "cancelled";
    } else if (status === ACTIVE_STATUS[section]) {
      stages[section] = "active";
    } else {
      stages[section] = "pending";
    }
  });

  return stages;
};

const PROGRESS_LABELS = {
  pengajuan: "Pengajuan",
  analisis: "Analisis",
  pembelian: "Pembelian",
  penerimaan: "Penerimaan",
  kelengkapan: "Kelengkapan",
};

// one line about where the request is, for the mobile list: "Langkah 3 dari 5 · Pembelian"
export const getProgressText = (data) => {
  if (data?.status === -1) return "Ditolak";

  const sections = Object.keys(PROGRESS_LABELS);
  const stages = getSectionStages(data);
  const activeIndex = sections.findIndex((key) => stages[key] === "active");

  if (activeIndex < 0) return `Selesai · ${sections.length} dari ${sections.length}`;
  return `Langkah ${activeIndex + 1} dari ${sections.length} · ${
    PROGRESS_LABELS[sections[activeIndex]]
  }`;
};

export const generateNewPermintaanMinyak = () => ({
  nomor: "",
  tanggal: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
  jumlah_diminta: 0,
  penggunaan: "",
  status: 0,
});

const isEmpty = (value) =>
  value === undefined || value === null || String(value).trim() === "";

/**
 * Validate the fields of one section.
 * strict = also the fields needed before the section can be signed.
 * returns an object { field: message }, empty when valid.
 */
export const validateSection = (section, data, strict) => {
  const error = {};

  switch (section) {
    case "pengajuan":
      if (!data.tanggal) error.tanggal = "Tanggal tidak boleh kosong";
      if (!data.department_id)
        error.department_id = "Department harus dipilih";
      if (!data.jenis_minyak_id)
        error.jenis_minyak_id = "Jenis minyak harus dipilih";
      if (!data.jumlah_diminta || data.jumlah_diminta <= 0)
        error.jumlah_diminta = "Jumlah diminta harus lebih dari 0";
      if (isEmpty(data.penggunaan))
        error.penggunaan = "Penggunaan tidak boleh kosong";
      break;

    case "analisis":
      if (data.status_analisis === 1) {
        if (!data.jumlah_disetujui || data.jumlah_disetujui <= 0) {
          error.jumlah_disetujui = "Jumlah disetujui harus lebih dari 0";
        } else if (data.jumlah_disetujui > data.jumlah_diminta) {
          error.jumlah_disetujui = `Tidak boleh melebihi jumlah diminta (${Number(
            data.jumlah_diminta
          ).toLocaleString("id-ID")} L)`;
        }
      } else if (data.status_analisis === -1) {
        if (isEmpty(data.alasan_penolakan))
          error.alasan_penolakan = "Alasan penolakan harus diisi";
      } else if (strict) {
        error.status_analisis = "Pilih Setuju atau Tolak";
      }
      break;

    case "pembelian":
      if (strict) {
        if (!data.tanggal_beli) error.tanggal_beli = "Tanggal beli harus diisi";
        if (isEmpty(data.bon_spbu)) error.bon_spbu = "No bon SPBU harus diisi";
        if (isEmpty(data.gambar_bon_spbu))
          error.gambar_bon_spbu = "Foto bon SPBU harus diambil";
      }
      break;

    case "penerimaan":
      if (strict) {
        if (!data.jumlah_diterima || data.jumlah_diterima <= 0)
          error.jumlah_diterima = "Jumlah diterima harus lebih dari 0";
      }
      break;

    case "kelengkapan":
      if (data.is_doc_npp > 0 && isEmpty(data.nomor_npp))
        error.nomor_npp = "Nomor NPP harus diisi";
      break;

    default:
      break;
  }

  return error;
};

// only the fields of one section, in the format of the API
export const buildSectionBody = (section, data) => {
  const body = { id: data.id ?? null };

  SECTION_FIELDS[section].forEach((field) => {
    let value = data[field];

    if (section === "pengajuan" && field === "nomor") {
      value = value ?? "";
    } else if (field === "tanggal" || field === "tanggal_beli") {
      // date columns
      value = value ? String(value).substring(0, 10) : null;
    } else if (field.startsWith("is_")) {
      value = value > 0 ? 1 : 0;
    } else if (value === undefined) {
      value = null;
    }

    body[field] = value;
  });

  return body;
};

const formatLiter = (value) =>
  `${Number(value ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 2 })} L`;

// one line about a section, shown while it is closed
export const getSectionSummary = (section, data, stage) => {
  if (stage === "pending") return "Belum dimulai";
  if (stage === "cancelled") return "Dibatalkan";

  const parts = [];

  switch (section) {
    case "pengajuan":
      if (data.jumlah_diminta)
        parts.push(
          `${formatLiter(data.jumlah_diminta)} ${
            data.data_jenis_minyak?.nama ?? ""
          }`.trim()
        );
      if (data.data_department?.nama) parts.push(data.data_department.nama);
      if (data.data_verif_diajukan_oleh?.nama)
        parts.push(data.data_verif_diajukan_oleh.nama);
      break;

    case "analisis":
      if (data.status_analisis === -1) {
        parts.push("Ditolak");
        if (data.alasan_penolakan) parts.push(data.alasan_penolakan);
      } else if (data.status_analisis === 1) {
        parts.push(`Disetujui ${formatLiter(data.jumlah_disetujui)}`);
      }
      if (data.data_verif_dianalisa_oleh?.nama)
        parts.push(data.data_verif_dianalisa_oleh.nama);
      break;

    case "pembelian":
      if (data.bon_spbu) parts.push(`Bon ${data.bon_spbu}`);
      if (data.tanggal_beli)
        parts.push(`Beli ${String(data.tanggal_beli).substring(0, 10)}`);
      if (data.data_verif_diproses_oleh?.nama)
        parts.push(data.data_verif_diproses_oleh.nama);
      break;

    case "penerimaan":
      if (data.jumlah_diterima)
        parts.push(`Diterima ${formatLiter(data.jumlah_diterima)}`);
      if (data.data_verif_diterima?.nama)
        parts.push(data.data_verif_diterima.nama);
      break;

    case "kelengkapan": {
      const complete = [
        data.is_doc_form_permintaan,
        data.is_doc_bon_spbu_asli,
        data.is_doc_npp,
      ].filter((value) => value > 0).length;
      parts.push(`${complete}/3 dokumen lengkap`);
      if (data.data_verif_diarsip_oleh?.nama)
        parts.push(data.data_verif_diarsip_oleh.nama);
      break;
    }

    default:
      break;
  }

  return parts.join(" · ");
};
